import {
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core'
import * as d3 from 'd3'
import { RecoveryPointAttemptDto } from 'src/app/services/dtos'
import { dateDiff, durationText } from 'src/app/shared/utils'

function hasReplicationLatency(rpaDto: RecoveryPointAttemptDto) {
  return rpaDto.maxReplicationLatency !== null && rpaDto.maxReplicationLatency !== undefined
}

// Colors matching _style-vars.scss — used in D3/SVG where SCSS vars aren't available
const ARPIO_BLUE = '#00a9be' // $arpio-blue
const DARK_COLOR = '#2b303a' // $dark-color
const LIGHT_GRAY = '#6b7280' // $light-gray
const MEDIUM_COLOR = '#e8e8e8' // $medium-color (banner background)

// Unique ID counter for SVG defs (gradient, clip-path) to avoid collisions
let instanceCounter = 0

@Component({
  selector: 'rpo-chart',
  templateUrl: './rpo-chart.component.html',
  styleUrls: ['./rpo-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class RpoChartComponent implements OnInit, OnChanges, OnDestroy {
  constructor(private zone: NgZone) {
    // Generate unique IDs for SVG defs so multiple chart instances don't collide
    const id = ++instanceCounter
    this.gradientId = `rpo-gradient-${id}`
    this.replicationGradientId = `rpo-replication-gradient-${id}`
    this.clipId = `rpo-clip-${id}`
  }

  @Input() rpo: number = null
  @Input() recoveryPointAttempts: Array<RecoveryPointAttemptDto> = null

  @ViewChild('chartContainer', { static: true }) containerRef: ElementRef<HTMLDivElement>
  @ViewChild('chartSvg', { static: true }) svgRef: ElementRef<SVGSVGElement>
  @ViewChild('tooltip', { static: true }) tooltipRef: ElementRef<HTMLDivElement>

  readonly gradientId: string
  readonly replicationGradientId: string
  readonly clipId: string

  private showReplicationLatencies = false
  private resizeObserver: ResizeObserver
  private width = 0
  private height = 0

  // Active time range preset — defaults to 1 day view
  activeRange: '1d' | '3d' | '1w' | '2w' | null = '1d'
  // Whether any data point falls inside the currently visible x-window.
  // Drives the "No recent data to display" overlay so the user understands
  // why the chart looks empty — e.g. selected 1W but backups only started yesterday.
  hasVisibleData = true
  // Most recent real attempt's createSuccess across the dataset, or null.
  // Drives the empty-state overlay copy. Tracked separately from
  // snapshotData so the synthetic now-anchor doesn't pollute it.
  private latestDataTime: number | null = null
  // Zoom scale `k` that corresponds to the current preset. Panning (translate-only)
  // keeps this scale, so the preset stays "applied"; wheel-zoom changes `k` and
  // clears the preset. Defaults to 1 because baseXScale is built to show a 1-day
  // window at identity transform (k=1), matching the initial `activeRange = '1d'`.
  private activeRangeScale: number | null = 1

  // D3 state
  private svg: d3.Selection<SVGSVGElement, unknown, null, undefined>
  private chartGroup: d3.Selection<SVGGElement, unknown, null, undefined>
  private zoomBehavior: d3.ZoomBehavior<SVGSVGElement, unknown>
  private baseXScale: d3.ScaleTime<number, number> // unzoomed reference scale
  private xScale: d3.ScaleTime<number, number> // current (possibly zoomed) scale
  private ySnapshotScale: d3.ScaleLinear<number, number>
  private yReplicationScale: d3.ScaleLinear<number, number>
  private currentTransform: d3.ZoomTransform = d3.zoomIdentity

  // Cached data for tooltip lookups
  private snapshotData: Array<{ x: number; y: number }> = []
  private replicationData: Array<{ x: number; y: number }> = []

  // Scale parameters cached for zoom updates
  private earliestDataTime: number
  private nowTime: number

  private get displayRpoLine() {
    return this.rpo > 0
  }

  private get successfulRecoveryPointAttempts() {
    return this.recoveryPointAttempts.filter((rpa) => !!rpa.createSuccess)
  }

  ngOnInit() {
    this.svg = d3.select(this.svgRef.nativeElement)
    this.setupResizeObserver()
    this.renderChart()
  }

  ngOnChanges(changes: SimpleChanges) {
    this.showReplicationLatencies = this.rpo === 0 && this.recoveryPointAttempts?.some(hasReplicationLatency)
    if (this.svg && this.recoveryPointAttempts) {
      this.renderChart()
    }
  }

  ngOnDestroy() {
    this.resizeObserver?.disconnect()
    // Remove D3 zoom listeners to prevent memory leaks
    if (this.svg) {
      this.svg.on('.zoom', null)
    }
  }

  private setupResizeObserver() {
    this.resizeObserver = new ResizeObserver(() => {
      this.zone.run(() => {
        this.updateDimensions()
        this.renderChart()
      })
    })
    this.resizeObserver.observe(this.containerRef.nativeElement)
  }

  private updateDimensions() {
    const rect = this.containerRef.nativeElement.getBoundingClientRect()
    this.width = rect.width
    this.height = rect.height
  }

  // ===================== MAIN RENDER =====================

  private renderChart() {
    if (!this.recoveryPointAttempts || !this.svg) return

    this.updateDimensions()
    if (this.width <= 0 || this.height <= 0) return

    // Clear previous content
    this.svg.selectAll('*').remove()

    // ─── Compute data (unchanged logic) ───
    const snapshotPercent = 0.9
    const replicationPercent = snapshotPercent

    const snapshotPercentLine = this.computeXPercentSnapshotDataScale(snapshotPercent)
    const snapshotYMax = snapshotPercentLine / 0.6
    const rpoYMax = this.rpo / 0.8
    // curveMonotoneX doesn't overshoot, so less headroom needed than tension: 0.3
    const ymax = Math.max(snapshotYMax, rpoYMax) * 1.05

    this.nowTime = Date.now()
    const successfulAttempts = this.successfulRecoveryPointAttempts
    this.earliestDataTime =
      successfulAttempts.length > 0
        ? new Date(successfulAttempts[0].createSuccess).getTime()
        : this.nowTime - 1000 * 60 * 60 * 24

    this.snapshotData = this.computeRpoData()
    this.replicationData = this.showReplicationLatencies ? this.computeRealTimeData() : []

    // Capture the most recent data-point time across both series so the
    // overlay can quantify "Latest data was X ago" when nothing is visible.
    this.latestDataTime = this.computeLatestDataTime()

    // Auto-pick the time-range preset on every render so the chart never
    // sits on an empty window: if the current preset has data, keep it
    // (respects the user's last manual click); otherwise escalate to the
    // smallest preset that does. Re-runs on every data refresh, so an app
    // whose latest backup ages out of the 1D window auto-bumps up to 3D /
    // 1W / 2W as needed. A null activeRange means the user wheel-zoomed
    // to a custom view; the chart is being fully rebuilt, so we can't
    // preserve their zoom regardless — fall back to the auto-picked preset.
    this.activeRange = this.pickAppropriateTimeRange()
    const presetDays = { '1d': 1, '3d': 3, '1w': 7, '2w': 14 }
    const defaultVisibleWindow = presetDays[this.activeRange] * 24 * 60 * 60 * 1000
    const idealVisibleMin = this.nowTime - defaultVisibleWindow
    const visibleMin = Math.max(this.earliestDataTime, idealVisibleMin)
    // Visible window got capped to the data range — narrower than the
    // preset implies. Clear activeRange so no button looks selected and
    // clicking a preset actually expands the view.
    if (visibleMin > idealVisibleMin) {
      this.activeRange = null
      this.activeRangeScale = null
    }

    // ─── Build scales ───
    this.baseXScale = d3.scaleTime().domain([visibleMin, this.nowTime]).range([0, this.width])
    this.xScale = this.baseXScale.copy()

    this.ySnapshotScale = d3.scaleLinear().domain([0, ymax]).range([this.height, 0])

    if (this.showReplicationLatencies) {
      const realTimeMax = this.computeXPercentRealTimeDataScale(replicationPercent)
      let yScaleMax = realTimeMax / 0.25
      let yScaleMin = 0
      if (yScaleMax === 0) {
        yScaleMax = 1
        yScaleMin = yScaleMax * -0.05
      }
      this.yReplicationScale = d3.scaleLinear().domain([yScaleMin, yScaleMax]).range([this.height, 0])
    }

    // ─── SVG defs: gradient + clip path ───
    const defs = this.svg.append('defs')

    // Vertical gradient for snapshot area fill
    const gradient = defs
      .append('linearGradient')
      .attr('id', this.gradientId)
      .attr('x1', 0)
      .attr('y1', 0)
      .attr('x2', 0)
      .attr('y2', 1)
    gradient.append('stop').attr('offset', '0%').attr('stop-color', ARPIO_BLUE).attr('stop-opacity', 0.25)
    gradient.append('stop').attr('offset', '100%').attr('stop-color', ARPIO_BLUE).attr('stop-opacity', 0.02)

    // Subtle grey gradient for replication latency area fill
    const repGradient = defs
      .append('linearGradient')
      .attr('id', this.replicationGradientId)
      .attr('x1', 0)
      .attr('y1', 0)
      .attr('x2', 0)
      .attr('y2', 1)
    repGradient.append('stop').attr('offset', '0%').attr('stop-color', DARK_COLOR).attr('stop-opacity', 0.12)
    repGradient.append('stop').attr('offset', '100%').attr('stop-color', DARK_COLOR).attr('stop-opacity', 0.01)

    // Clip path to contain paths within chart area during pan/zoom
    defs.append('clipPath').attr('id', this.clipId).append('rect').attr('width', this.width).attr('height', this.height)

    // ─── Chart group (clipped) ───
    this.chartGroup = this.svg.append('g').attr('clip-path', `url(#${this.clipId})`)

    // ─── Render layers ───
    this.renderSnapshotArea()
    this.renderSnapshotLine()
    if (this.showReplicationLatencies) {
      this.renderReplicationLine()
    }
    this.renderAnnotations(snapshotPercent, snapshotPercentLine, replicationPercent)

    // ─── Interactivity ───
    this.setupZoom()
    this.setupTooltip()

    // Seed the "no data in view" flag now that snapshotData + xScale are in place;
    // setTimeRange() keeps it fresh on subsequent preset changes, and the zoom handler
    // keeps it fresh on manual pan/wheel-zoom.
    this.updateHasVisibleData()
  }

  // ===================== RENDERING =====================

  private renderSnapshotArea() {
    const area = d3
      .area<{ x: number; y: number }>()
      .defined((d) => d.y != null)
      .x((d) => this.xScale(d.x))
      .y0(this.height)
      .y1((d) => this.ySnapshotScale(d.y))
      .curve(d3.curveMonotoneX)

    this.chartGroup
      .append('path')
      .datum(this.snapshotData)
      .attr('class', 'snapshot-area')
      .attr('d', area)
      .attr('fill', `url(#${this.gradientId})`)
  }

  private renderSnapshotLine() {
    const line = d3
      .line<{ x: number; y: number }>()
      .defined((d) => d.y != null)
      .x((d) => this.xScale(d.x))
      .y((d) => this.ySnapshotScale(d.y))
      .curve(d3.curveMonotoneX)

    this.chartGroup
      .append('path')
      .datum(this.snapshotData)
      .attr('class', 'snapshot-line')
      .attr('d', line)
      .attr('fill', 'none')
      .attr('stroke', ARPIO_BLUE)
      .attr('stroke-opacity', 0.6)
      .attr('stroke-width', 2)
  }

  private renderReplicationLine() {
    // Area fill — subtle grey gradient beneath the replication latency line
    const area = d3
      .area<{ x: number; y: number }>()
      .x((d) => this.xScale(d.x))
      .y0(this.height)
      .y1((d) => this.yReplicationScale(d.y))

    this.chartGroup
      .append('path')
      .datum(this.replicationData)
      .attr('class', 'replication-area')
      .attr('d', area)
      .attr('fill', `url(#${this.replicationGradientId})`)

    // Stroke line on top of the fill
    const line = d3
      .line<{ x: number; y: number }>()
      .x((d) => this.xScale(d.x))
      .y((d) => this.yReplicationScale(d.y))

    this.chartGroup
      .append('path')
      .datum(this.replicationData)
      .attr('class', 'replication-line')
      .attr('d', line)
      .attr('fill', 'none')
      .attr('stroke', DARK_COLOR)
      .attr('stroke-opacity', 0.5)
      .attr('stroke-width', 1.5)
  }

  private renderAnnotations(snapshotPercent: number, snapshotPercentLine: number, replicationPercent: number) {
    // Annotations go in an unclipped group so labels aren't cut off
    const annotationGroup = this.svg.append('g').attr('class', 'annotations')

    if (this.displayRpoLine) {
      // RPO target line
      this.drawAnnotationLine(annotationGroup, this.ySnapshotScale(this.rpo), `RPO: ${durationText(this.rpo)}`)
    } else {
      // P90 snapshot age line
      this.drawAnnotationLine(
        annotationGroup,
        this.ySnapshotScale(snapshotPercentLine),
        `${snapshotPercent * 100}% Snapshot Age: ${durationText(snapshotPercentLine, true)}`
      )
    }

    if (this.showReplicationLatencies) {
      const realTimeMax = this.computeXPercentRealTimeDataScale(replicationPercent)
      this.drawAnnotationLine(
        annotationGroup,
        this.yReplicationScale(realTimeMax),
        `${replicationPercent * 100}% Replication Latency: ${durationText(realTimeMax, true)}`
      )
    }
  }

  private drawAnnotationLine(group: d3.Selection<SVGGElement, unknown, null, undefined>, y: number, label: string) {
    // Dashed horizontal line
    group
      .append('line')
      .attr('x1', 0)
      .attr('x2', this.width)
      .attr('y1', y)
      .attr('y2', y)
      .attr('stroke', DARK_COLOR)
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3')

    // Render text invisibly first to measure its bounding box
    const textEl = group
      .append('text')
      .attr('x', this.width - 4)
      .attr('y', y - 5)
      .attr('text-anchor', 'end')
      .style('font-family', "'Source Sans 3', sans-serif")
      .style('font-size', '11px')
      .style('font-weight', '600')
      .attr('fill', DARK_COLOR)
      .text(label)

    // Seamless gradient backdrop behind the label text so data lines don't reduce legibility.
    // Wide horizontal fade from fully transparent to banner bg, with soft vertical feathering.
    const bbox = (textEl.node() as SVGTextElement).getBBox()
    const fadeWidth = 80 // generous fade region for a seamless blend
    const vPad = 4 // vertical padding above/below text
    const bgGradientId = `label-bg-${this.clipId}-${Math.round(y)}`

    const grad = this.svg
      .select('defs')
      .append('linearGradient')
      .attr('id', bgGradientId)
      .attr('x1', '0')
      .attr('y1', '0')
      .attr('x2', '1')
      .attr('y2', '0')
    // Long gentle ramp from fully transparent to opaque
    grad.append('stop').attr('offset', '0%').attr('stop-color', MEDIUM_COLOR).attr('stop-opacity', 0)
    grad.append('stop').attr('offset', '40%').attr('stop-color', MEDIUM_COLOR).attr('stop-opacity', 0.4)
    grad.append('stop').attr('offset', '100%').attr('stop-color', MEDIUM_COLOR).attr('stop-opacity', 0.7)

    // SVG blur filter for soft feathered edges on the backdrop
    const filterId = `label-blur-${this.clipId}-${Math.round(y)}`
    this.svg
      .select('defs')
      .append('filter')
      .attr('id', filterId)
      // Expand filter region so blur isn't clipped at edges
      .attr('x', '-20%')
      .attr('y', '-20%')
      .attr('width', '140%')
      .attr('height', '140%')
      .append('feGaussianBlur')
      .attr('stdDeviation', '4')

    // Insert the backdrop rect BEFORE the text so text renders on top
    group
      .insert('rect', 'text')
      .attr('x', bbox.x - fadeWidth)
      .attr('y', bbox.y - vPad)
      .attr('width', bbox.width + fadeWidth)
      .attr('height', bbox.height + vPad * 2)
      .attr('rx', 6)
      .attr('fill', `url(#${bgGradientId})`)
      .attr('filter', `url(#${filterId})`)
  }

  // ===================== ZOOM / PAN =====================

  private setupZoom() {
    // Scale extent: 1x = default 3-day view, max zoom shows 1 hour
    const defaultRange = this.baseXScale.domain()[1].getTime() - this.baseXScale.domain()[0].getTime()
    const minRange = 1000 * 60 * 60 // 1 hour minimum zoom
    const maxScale = Math.max(defaultRange / minRange, 1)

    // Compute how wide the full data range would be at the base scale,
    // so translateExtent allows panning across the entire history
    const fullPixelWidth = this.baseXScale(this.nowTime) - this.baseXScale(this.earliestDataTime)

    this.zoomBehavior = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent([1, maxScale])
      // translateExtent: the pannable world. Left edge = earliest data pixel, right edge = now pixel.
      // This allows panning left to see older data beyond the default 3-day window.
      .translateExtent([
        [this.baseXScale(this.earliestDataTime), 0],
        [this.baseXScale(this.nowTime), this.height],
      ])
      .extent([
        [0, 0],
        [this.width, this.height],
      ])
      .filter((event: any) => {
        // Allow all wheel events (zoom) and left-click drag (pan); block right-click
        if (event.type === 'wheel') return true
        return !event.button
      })
      .on('start', (event) => {
        if (event.sourceEvent) {
          this.containerRef.nativeElement.classList.add('panning')
        }
      })
      .on('zoom', (event) => {
        this.currentTransform = event.transform
        this.xScale = this.currentTransform.rescaleX(this.baseXScale) as d3.ScaleTime<number, number>
        this.updatePaths()
        this.updateHasVisibleData()
        // Presets describe the window *width* (1D/3D/1W/2W), not its position.
        // Pure panning preserves the scale `k`, so the preset is still accurate
        // and should stay selected. Only clear when the scale diverges from the
        // preset's scale (e.g. wheel-zoom). `sourceEvent` guards against the
        // programmatic transition from setTimeRange() clearing itself.
        if (event.sourceEvent && this.activeRangeScale !== null) {
          const scaleDrift = Math.abs(event.transform.k - this.activeRangeScale)
          if (scaleDrift > 0.001) {
            this.activeRange = null
            this.activeRangeScale = null
          }
        }
      })
      .on('end', () => {
        this.containerRef.nativeElement.classList.remove('panning')
      })

    this.svg.call(this.zoomBehavior)
    // Prevent double-click zoom — it's unexpected on a sparkline chart
    this.svg.on('dblclick.zoom', null)
  }

  /**
   * True if any real attempt's createSuccess falls in the visible x-window.
   * Probes attempts directly (not `snapshotData`) so the synthetic now-anchor
   * appended by `computeNotBriefRpoData` can't racily satisfy the check.
   */
  private updateHasVisibleData() {
    const [min, max] = this.xScale.domain().map((d) => d.getTime())
    const snapshotVisible = this.successfulRecoveryPointAttempts.some((att) => {
      const t = new Date(att.createSuccess).getTime()
      return t >= min && t <= max
    })
    const replicationVisible =
      this.showReplicationLatencies &&
      this.recoveryPointAttempts.some((att) => {
        if (!hasReplicationLatency(att)) return false
        const publishTime = att.createSuccess ?? att.createFailure
        if (!publishTime) return false
        const t = new Date(publishTime).getTime()
        return t >= min && t <= max
      })
    this.hasVisibleData = snapshotVisible || replicationVisible
  }

  /** Re-render all data paths and annotations using the current (zoomed) x-scale */
  private updatePaths() {
    // Snapshot area
    const area = d3
      .area<{ x: number; y: number }>()
      .defined((d) => d.y != null)
      .x((d) => this.xScale(d.x))
      .y0(this.height)
      .y1((d) => this.ySnapshotScale(d.y))
      .curve(d3.curveMonotoneX)

    this.chartGroup.select('.snapshot-area').datum(this.snapshotData).attr('d', area)

    // Snapshot line
    const line = d3
      .line<{ x: number; y: number }>()
      .defined((d) => d.y != null)
      .x((d) => this.xScale(d.x))
      .y((d) => this.ySnapshotScale(d.y))
      .curve(d3.curveMonotoneX)

    this.chartGroup.select('.snapshot-line').datum(this.snapshotData).attr('d', line)

    // Replication area + line
    if (this.showReplicationLatencies) {
      const repArea = d3
        .area<{ x: number; y: number }>()
        .x((d) => this.xScale(d.x))
        .y0(this.height)
        .y1((d) => this.yReplicationScale(d.y))

      this.chartGroup.select('.replication-area').datum(this.replicationData).attr('d', repArea)

      const repLine = d3
        .line<{ x: number; y: number }>()
        .x((d) => this.xScale(d.x))
        .y((d) => this.yReplicationScale(d.y))

      this.chartGroup.select('.replication-line').datum(this.replicationData).attr('d', repLine)
    }

    // Annotations don't move with zoom (they're horizontal lines at fixed y-values)
    // so no update needed for them
  }

  // ===================== TOOLTIP =====================

  private setupTooltip() {
    const tooltipEl = this.tooltipRef.nativeElement
    const bisector = d3.bisector<{ x: number; y: number }, number>((d) => d.x).left

    // Use the SVG element itself for tooltip mouse tracking — no overlay rect needed,
    // which avoids blocking drag events from reaching the d3.zoom behavior
    this.svg.on('mousemove.tooltip', (event: MouseEvent) => {
      const [mouseX] = d3.pointer(event, this.svgRef.nativeElement)
      const xValue = this.xScale.invert(mouseX).getTime()

      // Find nearest snapshot data point
      const validSnapshot = this.snapshotData.filter((d) => d.y != null)
      const si = bisector(validSnapshot, xValue)
      const s0 = validSnapshot[si - 1]
      const s1 = validSnapshot[si]
      const nearestSnapshot = s0 && s1 ? (xValue - s0.x < s1.x - xValue ? s0 : s1) : s0 || s1

      // Find nearest replication data point (if applicable)
      let nearestReplication: { x: number; y: number } | null = null
      if (this.showReplicationLatencies && this.replicationData.length > 0) {
        const ri = bisector(this.replicationData, xValue)
        const r0 = this.replicationData[ri - 1]
        const r1 = this.replicationData[ri]
        nearestReplication = r0 && r1 ? (xValue - r0.x < r1.x - xValue ? r0 : r1) : r0 || r1
      }

      if (!nearestSnapshot) {
        tooltipEl.style.opacity = '0'
        return
      }

      // Build tooltip content
      const title = new Date(nearestSnapshot.x).toLocaleString()
      const snapshotLabel = this.displayRpoLine
        ? `Recovery Point Age: ${durationText(nearestSnapshot.y)}`
        : `Snapshot Age: ${durationText(nearestSnapshot.y)}`

      let body = `<div class="tooltip-title">${title}</div>`
      body += `<div class="tooltip-body">${snapshotLabel}</div>`
      if (nearestReplication) {
        body += `<div class="tooltip-body">Replication Latency: ${durationText(nearestReplication.y, true)}</div>`
      }
      tooltipEl.innerHTML = body

      // Position tooltip using fixed coords to escape parent overflow: hidden
      const containerRect = this.containerRef.nativeElement.getBoundingClientRect()
      const px = this.xScale(nearestSnapshot.x)
      const py = this.ySnapshotScale(nearestSnapshot.y)
      const viewportX = containerRect.left + px
      const viewportY = containerRect.top + py
      const flipThreshold = this.width * 0.5

      if (px > flipThreshold) {
        tooltipEl.style.right = `${window.innerWidth - viewportX + 10}px`
        tooltipEl.style.left = 'auto'
      } else {
        tooltipEl.style.left = `${viewportX + 10}px`
        tooltipEl.style.right = 'auto'
      }
      tooltipEl.style.top = `${viewportY - 10}px`
      tooltipEl.style.opacity = '1'
    })
    this.svg.on('mouseleave.tooltip', () => {
      tooltipEl.style.opacity = '0'
    })
  }

  // ===================== TIME RANGE PRESETS =====================

  /**
   * Pick the time-range preset that should be active for the current data:
   *   1. If the current `activeRange` has data, keep it (so a manual
   *      preset click survives data refreshes that don't change which
   *      window has data).
   *   2. Otherwise walk 1D → 3D → 1W → 2W and return the smallest preset
   *      with data — auto-escalating apps whose latest backup just aged
   *      past the current window.
   *   3. If no preset has data but data exists somewhere older than 2W,
   *      return '2w' (widest). The user opens maxed-out so the only path
   *      to the older data is panning, which the overlay copy points to.
   *   4. If no data anywhere, return '1d'. Smallest preset for the empty
   *      case; the overlay communicates "no recent data".
   *
   * Runs on every renderChart, so this is the single place that decides
   * "what window should we be on right now?" — initial load, data refresh,
   * and resize all flow through it.
   */
  private pickAppropriateTimeRange(): '1d' | '3d' | '1w' | '2w' {
    const presets: Array<{ range: '1d' | '3d' | '1w' | '2w'; ms: number }> = [
      { range: '1d', ms: 1000 * 60 * 60 * 24 },
      { range: '3d', ms: 1000 * 60 * 60 * 24 * 3 },
      { range: '1w', ms: 1000 * 60 * 60 * 24 * 7 },
      { range: '2w', ms: 1000 * 60 * 60 * 24 * 14 },
    ]
    // Probe real attempts directly. Using `snapshotData` would let the
    // synthetic now-anchor (from computeNotBriefRpoData) racily count as
    // "in window" and lock auto-pick onto an empty 1D for stale apps.
    const hasDataIn = (windowMs: number): boolean => {
      const min = this.nowTime - windowMs
      const snapshotIn = this.successfulRecoveryPointAttempts.some((att) => {
        const t = new Date(att.createSuccess).getTime()
        return t >= min && t <= this.nowTime
      })
      if (snapshotIn) return true
      if (!this.showReplicationLatencies) return false
      return this.recoveryPointAttempts.some((att) => {
        if (!hasReplicationLatency(att)) return false
        const publishTime = att.createSuccess ?? att.createFailure
        if (!publishTime) return false
        const t = new Date(publishTime).getTime()
        return t >= min && t <= this.nowTime
      })
    }
    // Respect the user's current preset if it still has data.
    const current = presets.find((p) => p.range === this.activeRange)
    if (current && hasDataIn(current.ms)) return current.range
    // Otherwise escalate to the smallest preset with data.
    for (const p of presets) {
      if (hasDataIn(p.ms)) return p.range
    }
    return this.latestDataTime != null ? '2w' : '1d'
  }

  /**
   * Most recent real attempt's createSuccess, or `null` if none. Bypasses
   * `snapshotData` whose synthetic now-anchor would make stale apps
   * report their latest data as "seconds ago".
   */
  private computeLatestDataTime(): number | null {
    let latest: number | null = null
    const consider = (t: number) => {
      if (latest === null || t > latest) latest = t
    }
    for (const att of this.successfulRecoveryPointAttempts) {
      consider(new Date(att.createSuccess).getTime())
    }
    if (this.showReplicationLatencies) {
      for (const att of this.recoveryPointAttempts) {
        if (hasReplicationLatency(att)) {
          const publishTime = att.createSuccess ?? att.createFailure
          if (publishTime) consider(new Date(publishTime).getTime())
        }
      }
    }
    return latest
  }

  /**
   * Overlay text shown when the visible window has no data. Quantifies
   * "No data for X" for the stale-data case so the user knows the gap
   * length at a glance; falls back to a generic message when nothing
   * exists at all (e.g. brand-new app, all-failed attempts).
   */
  get noDataMessage(): string {
    if (this.latestDataTime == null) return 'No recent data to display'
    const ageSec = Math.round((Date.now() - this.latestDataTime) / 1000)
    return `No data for ${durationText(ageSec)}. Pan or zoom out to view.`
  }

  /** Zoom the chart to show a fixed time window ending at now. */
  setTimeRange(range: '1d' | '3d' | '1w' | '2w') {
    if (!this.svg || !this.zoomBehavior || !this.baseXScale) return
    if (this.activeRange === range) return // Already showing this range

    this.activeRange = range
    const durations = { '1d': 1, '3d': 3, '1w': 7, '2w': 14 }
    const durationMs = durations[range] * 24 * 60 * 60 * 1000

    // Compute the zoom transform that maps [now - duration, now] to [0, width]
    const defaultRange = this.baseXScale.domain()[1].getTime() - this.baseXScale.domain()[0].getTime()
    const k = defaultRange / durationMs
    // Translate so the right edge aligns with "now"
    const tx = this.width - k * this.baseXScale(this.nowTime)

    // Remember the preset's scale so the zoom handler can distinguish pan
    // (scale unchanged → keep preset) from wheel-zoom (scale changed → clear).
    this.activeRangeScale = k

    const transform = d3.zoomIdentity.translate(tx, 0).scale(k)
    this.svg.transition().duration(300).call(this.zoomBehavior.transform, transform)
  }

  // ===================== DATA COMPUTATION (unchanged) =====================

  private computeRpoData() {
    if (!this.recoveryPointAttempts) {
      return []
    }

    const successfulAttempts = this.successfulRecoveryPointAttempts
    let data = []
    // if the application has been around for less than 10 RPO cycles, we draw a sawtooth.
    // otherwise, we just draw the peaks.
    const rpoCycleThreshold = 10
    const dateThreshold = new Date(Date.now() - rpoCycleThreshold * this.rpo * 1000)

    if (successfulAttempts.length > 0 && successfulAttempts[0].createSuccess > dateThreshold) {
      data = this.computeBriefRpoData(successfulAttempts, dateThreshold)
    } else if (successfulAttempts.length > 0) {
      data = this.computeNotBriefRpoData(successfulAttempts)
    }

    return data
  }

  private computeBriefRpoData(successfulAttempts: Array<RecoveryPointAttemptDto>, dateThreshold: Date) {
    const data: Array<{ x: number; y: number }> = []

    // insert a null value at the dateThreshold to establish the lower bounds of the x-axis
    data.push({ x: dateThreshold.getTime(), y: null })
    for (let i = 0; i < successfulAttempts.length - 1; i++) {
      // insert 1 datapoint for when the RP was published
      data.push({
        x: new Date(successfulAttempts[i].createSuccess).getTime(),
        y: dateDiff(successfulAttempts[i].createSuccess, successfulAttempts[i].timestamp) / 1000,
      })
      // insert a second datapoint for when the RP was replaced
      data.push({
        x: new Date(successfulAttempts[i + 1].createSuccess).getTime(),
        y: dateDiff(successfulAttempts[i + 1].createSuccess, successfulAttempts[i].timestamp) / 1000,
      })
    }

    // insert datapoints for the latest RP.  The algorithm is slightly different.
    const latestAttempt = successfulAttempts[successfulAttempts.length - 1]
    data.push({
      x: new Date(latestAttempt.createSuccess).getTime(),
      y: dateDiff(latestAttempt.createSuccess, latestAttempt.timestamp) / 1000,
    })

    const now = new Date()
    data.push({
      x: now.getTime(),
      y: dateDiff(now, latestAttempt.timestamp) / 1000,
    })

    return data
  }

  private computeNotBriefRpoData(successfulAttempts) {
    const data: Array<{ x: number; y: number }> = []
    for (let i = 0; i < successfulAttempts.length - 1; i++) {
      data.push({
        x: new Date(successfulAttempts[i + 1].createSuccess).getTime(),
        y: dateDiff(successfulAttempts[i + 1].createSuccess, successfulAttempts[i].timestamp) / 1000,
      })
    }

    // insert one final datapoint for now
    const now = new Date()
    data.push({
      x: now.getTime(),
      y: dateDiff(now, successfulAttempts[successfulAttempts.length - 1].timestamp) / 1000,
    })

    return data
  }

  private computeXPercentSnapshotDataScale(x: number) {
    const successfulAttempts = this.successfulRecoveryPointAttempts
    const snapshotAges: Array<number> = []
    for (let i = 1; i < successfulAttempts.length; i++) {
      snapshotAges.push(dateDiff(successfulAttempts[i].createSuccess, successfulAttempts[i - 1].timestamp) / 1000)
    }
    if (snapshotAges.length === 0) return 0 // guard: no data yet, avoid undefined → NaN propagation
    snapshotAges.sort((l, r) => l - r)
    const xPercentElement = Math.min(Math.floor(snapshotAges.length * x), snapshotAges.length - 1)
    return snapshotAges[xPercentElement]
  }

  private computeRealTimeData() {
    const data: Array<{ x: number; y: number }> = []
    for (let i = 0; i < this.recoveryPointAttempts.length; i++) {
      const attempt = this.recoveryPointAttempts[i]
      if (hasReplicationLatency(attempt)) {
        const publishTime = attempt.createSuccess ? attempt.createSuccess : attempt.createFailure
        data.push({
          x: new Date(publishTime).getTime(),
          y: attempt.maxReplicationLatency,
        })
      }
    }
    return data
  }

  private computeXPercentRealTimeDataScale(x: number) {
    // We want the real-time scale to sit below the non-real-time scale (since it should be faster).  We'll target having X% of
    // the real-time metrics sit at 25% on the graph.  So, this function computes the X% value, and multiplies that * 4
    // for the scale.

    // sort the data.  Array.sort() in Javscript mutates the array, so we use Array.slice() to copy it first.
    const filteredRealTimeData = this.recoveryPointAttempts.filter(hasReplicationLatency)
    const sortedRealTimeData = filteredRealTimeData.sort((l, r) => l.maxReplicationLatency - r.maxReplicationLatency)
    const ninetyPercentElement = Math.floor(sortedRealTimeData.length * x)
    return sortedRealTimeData[ninetyPercentElement].maxReplicationLatency
  }
}
